import type { BannerType } from '@sunwebgroup/ui-components'
import { useIntl } from '@/intl'
import { getIconForService } from '@/scopes/checkout/components/ServiceIcon'
import { Price, RoomName, Texts } from '@/scopes/checkout/components/services/ServiceComponents'
import { useSendAncillaryAvailable } from '@/scopes/checkout/domain/analytics/useSendAncillaryAvailable'
import { useService } from '@/scopes/checkout/domain/hooks'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { Banner, ListItem, RadioGroup, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import { Tag } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { updatePerRoomService } from '../../domain/actions'
import { createAncillariesClickedEvent } from '../../domain/analytics'
import { ServiceType } from '../../domain/types'
import ServiceBox from './ServiceBox'

export function BreakfastArrivalBusService() {
  const intl = useIntl()
  const { service, setServiceValue } = useService(ServiceType.BreakfastArrivalBus)
  const { sendEvent } = useAnalytics()
  useSendAncillaryAvailable(service, 'selectBreakfastArrivalBus')

  const handleValueChange = React.useCallback(
    (roomId: string, value: string) => {
      if (!service)
        return
      setServiceValue(updatePerRoomService({ roomId, value })(service))

      const optionClicked = service.options[value]
      sendEvent(
        createAncillariesClickedEvent({
          result: optionClicked?.price || 0,
          type: optionClicked?.label ? `${optionClicked?.label} for room ${roomId}` : '',
          description: 'selectBreakfastArrivalBus',
          element: 'radiobutton',
        }),
      )
    },
    [sendEvent, setServiceValue, service],
  )

  if (!service)
    return null

  const { rooms, banners } = service

  return (
    <ServiceBox
      title={intl.formatMessage({ id: 'checkout_steps_ancillaries_breakfast_arrival_bus_title' })}
      description={intl.formatMessage({ id: 'checkout_steps_ancillaries_breakfast_arrival_bus_description' })}
      glyph={getIconForService(ServiceType.BreakfastArrivalBus)}
    >
      <SpaceBetween direction="y" spacing={tk('spacing_sm')}>
        {Object.values(rooms ?? {}).map(room => (
          <RoomRow
            key={room.id}
            {...room}
            onValueChange={handleValueChange}
          />
        ))}

        {banners.map(({ title, text, type = 'info' }, index) => (
          <Banner key={`banner-${index}`} $type={type as BannerType}>
            <Texts direction="y">
              {title && <Typography $variant="label">{title}</Typography>}
              {text && <Typography dangerouslySetInnerHTML={{ __html: text }} />}
            </Texts>
          </Banner>
        ))}
      </SpaceBetween>
    </ServiceBox>
  )
}

function RoomRow({
  id,
  availableOptions,
  selectedValue,
  name,
  onValueChange,
}: {
  id: string
  availableOptions?: string[]
  selectedValue?: string
  name: string
  onValueChange: (roomId: string, value: string) => void
}) {
  const intl = useIntl()
  const { service } = useService(ServiceType.BreakfastArrivalBus)

  const handleValueChange = React.useCallback(
    (value: string) => {
      onValueChange(id, value)
    },
    [id, onValueChange],
  )

  if (!service)
    return null

  const { options, rooms } = service

  return (
    <SpaceBetween key={id} direction="y" spacing={tk('spacing_md')}>
      {Object.values(rooms ?? {}).length > 1 && (
        <RoomName as="p" $variant="body-sm-bold">
          {name}
        </RoomName>
      ) }
      <RadioGroup.Root value={selectedValue ?? null} onValueChange={handleValueChange}>
        <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
          {(availableOptions ?? []).map((optionId) => {
            const { value, label, badge, price } = options[optionId]

            return (
              <ListItem.Root key={optionId}>
                <ListItem.Control>
                  <RadioGroup.Item value={value} />
                </ListItem.Control>
                <ListItem.Content>
                  {badge && <Tag variant="success-tinted" size="M">{badge}</Tag>}
                  <ListItem.Label>{label}</ListItem.Label>
                </ListItem.Content>
                <ListItem.Trailing>
                  <Price as="span" $variant="label">
                    {intl.formatCurrency(price, price < 0)}
                  </Price>
                </ListItem.Trailing>
              </ListItem.Root>
            )
          })}
        </SpaceBetween>
      </RadioGroup.Root>
    </SpaceBetween>
  )
}
